import React from 'react';
import LandingHero from '../components/LandingHero';
import FeatureShowcase from '../components/FeatureShowcase';
import DemoGallery from '../components/DemoGallery';
import PricingCTA from '../components/PricingCTA';

/**
 * New conversion-optimized landing page
 * Implements ChatGPT's suggestions for better conversion rates
 */
export const NewLandingPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section with Live Demo */}
      <LandingHero />
      
      {/* Social Proof Bar */}
      <div className="bg-yellow-50 border-y border-yellow-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-8 text-center">
            <div className="flex items-center">
              <span className="text-yellow-600 mr-2">🚀</span>
              <span className="text-gray-700 font-semibold">10,000+ reels generated</span>
            </div>
            <div className="flex items-center">
              <span className="text-green-600 mr-2">⭐</span>
              <span className="text-gray-700 font-semibold">4.9/5 creator rating</span>
            </div>
            <div className="flex items-center">
              <span className="text-blue-600 mr-2">⚡</span>
              <span className="text-gray-700 font-semibold">95% time saved vs manual</span>
            </div>
            <div className="flex items-center">
              <span className="text-purple-600 mr-2">💰</span>
              <span className="text-gray-700 font-semibold">80% cost reduction</span>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Showcase */}
      <FeatureShowcase />

      {/* Demo Gallery - Before/After Results */}
      <DemoGallery />

      {/* Use Cases Section */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Perfect For Every Creator
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're just starting or scaling your content empire, 
              AutoReelr adapts to your needs and niche.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Faceless Creators */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="text-4xl mb-4">🎭</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Faceless Creators</h3>
              <p className="text-gray-600 mb-4">
                Create engaging content without ever appearing on camera. Perfect for 
                voice-over style content and privacy-focused creators.
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Educational content</li>
                <li>• Productivity tips</li>
                <li>• Financial advice</li>
                <li>• Tech tutorials</li>
              </ul>
            </div>

            {/* Course Creators */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="text-4xl mb-4">🎓</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Course Creators</h3>
              <p className="text-gray-600 mb-4">
                Promote your courses and build authority with professional 
                educational content that drives enrollments.
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Course previews</li>
                <li>• Testimonial highlights</li>
                <li>• Quick tips & insights</li>
                <li>• Behind-the-scenes</li>
              </ul>
            </div>

            {/* Agencies */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Agencies & Teams</h3>
              <p className="text-gray-600 mb-4">
                Scale your content production for multiple clients without 
                scaling your team size or overhead costs.
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Client content at scale</li>
                <li>• Brand consistency</li>
                <li>• Template workflows</li>
                <li>• Team collaboration</li>
              </ul>
            </div>

            {/* Small Businesses */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="text-4xl mb-4">🏪</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Small Businesses</h3>
              <p className="text-gray-600 mb-4">
                Professional marketing content without hiring expensive agencies 
                or full-time content creators.
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Product showcases</li>
                <li>• Service explanations</li>
                <li>• Customer testimonials</li>
                <li>• Industry insights</li>
              </ul>
            </div>

            {/* Side Hustlers */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="text-4xl mb-4">💪</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Side Hustlers</h3>
              <p className="text-gray-600 mb-4">
                Build your personal brand and grow your side business with 
                consistent, professional content creation.
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Authority building</li>
                <li>• Skill demonstrations</li>
                <li>• Success stories</li>
                <li>• Tips & tutorials</li>
              </ul>
            </div>

            {/* Content Repurposing */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="text-4xl mb-4">♻️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Content Repurposers</h3>
              <p className="text-gray-600 mb-4">
                Transform existing content into engaging short-form videos 
                for maximum reach across all platforms.
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Blog → Video conversion</li>
                <li>• Podcast highlights</li>
                <li>• Long-form → Short clips</li>
                <li>• Cross-platform optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              AutoReelr vs Traditional Methods
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See why thousands of creators have made the switch to AI-powered content creation
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Traditional Method */}
              <div className="p-8 lg:p-12">
                <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
                  <span className="mr-3">❌</span>
                  Traditional Content Creation
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <span className="text-red-500 mr-3 mt-1">⏰</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Time Intensive</h4>
                      <p className="text-gray-600 text-sm">3-5 hours per reel including planning, filming, editing</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-500 mr-3 mt-1">💰</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Expensive</h4>
                      <p className="text-gray-600 text-sm">$300-500 per reel with professional tools and services</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-500 mr-3 mt-1">🎯</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Skills Required</h4>
                      <p className="text-gray-600 text-sm">Video editing, design, copywriting, voice work</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-500 mr-3 mt-1">📉</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Inconsistent Quality</h4>
                      <p className="text-gray-600 text-sm">Results vary based on mood, energy, and available time</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-red-500 mr-3 mt-1">🔧</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Technical Barriers</h4>
                      <p className="text-gray-600 text-sm">Complex software, hardware requirements, learning curve</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AutoReelr Method */}
              <div className="p-8 lg:p-12 bg-gradient-to-br from-green-50 to-blue-50">
                <h3 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                  <span className="mr-3">✅</span>
                  AutoReelr AI Creation
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1">⚡</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Lightning Fast</h4>
                      <p className="text-gray-600 text-sm">Complete professional reels in under 5 minutes</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1">💎</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Affordable</h4>
                      <p className="text-gray-600 text-sm">$5-10 per reel with professional AI-generated content</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1">🎪</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">No Skills Needed</h4>
                      <p className="text-gray-600 text-sm">Just provide your topic - AI handles everything else</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1">📈</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Consistent Excellence</h4>
                      <p className="text-gray-600 text-sm">Professional quality every time with proven viral formulas</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1">🚀</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">Zero Barriers</h4>
                      <p className="text-gray-600 text-sm">Browser-based, no downloads, works on any device</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing and CTA */}
      <PricingCTA />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">AutoReelr</h3>
              <p className="text-gray-300 mb-4">
                The AI-powered platform that transforms your ideas into viral reels 
                in minutes, not hours.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-300 hover:text-white">Twitter</a>
                <a href="#" className="text-gray-300 hover:text-white">Instagram</a>
                <a href="#" className="text-gray-300 hover:text-white">YouTube</a>
                <a href="#" className="text-gray-300 hover:text-white">TikTok</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Demo</a></li>
                <li><a href="#" className="hover:text-white">API</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 mt-8 text-center text-gray-400">
            <p>&copy; 2024 AutoReelr. All rights reserved. Powered by Cloudflare Workers AI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPage;